import DraftsIcon from '@mui/icons-material/Drafts';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MouseIcon from '@mui/icons-material/Mouse';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import { Box, LinearProgress, Paper, Skeleton, Tooltip, Typography } from '@mui/material';

/**
 * Email campaign statistics from the API
 */
export interface EmailCampaignStatsData {
  total: number;
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  open_rate: number;
  click_rate: number;
  reply_rate: number;
}

interface EmailCampaignStatsProps {
  stats: EmailCampaignStatsData | null;
  loading?: boolean;
  emailCampaignId?: string;
}

interface StatItemProps {
  label: string;
  value: number;
  rate?: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const StatItem = ({ label, value, rate, icon, color, bgColor }: StatItemProps) => (
  <Tooltip title={rate !== undefined ? `${label}: ${value} (${rate}%)` : `${label}: ${value}`}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 1,
        backgroundColor: bgColor,
        minWidth: 100,
      }}
    >
      <Box sx={{ color }}>{icon}</Box>
      <Box>
        <Typography variant="body2" fontWeight={600} color={color}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
          {rate !== undefined && (
            <Typography component="span" variant="caption" color={color} sx={{ ml: 0.5 }}>
              ({rate}%)
            </Typography>
          )}
        </Typography>
      </Box>
    </Box>
  </Tooltip>
);

/**
 * EmailCampaignStats - Displays aggregate email campaign statistics
 *
 * Shows sent, opened, clicked, replied counts and rates in a horizontal bar
 * at the top of the campaign details page when email integration is enabled.
 */
export const EmailCampaignStats = ({ stats, loading = false, emailCampaignId }: EmailCampaignStatsProps) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'rgba(33, 150, 243, 0.05)',
          border: '1px solid rgba(33, 150, 243, 0.2)',
          borderRadius: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <MailOutlineIcon sx={{ color: '#2196f3' }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Email Campaign
          </Typography>
          <Box flex={1}>
            <Skeleton variant="text" width="80%" />
          </Box>
        </Box>
      </Paper>
    );
  }

  if (!stats) {
    return null;
  }

  // Calculate completion percentage (total processed / total leads)
  // With multi-step sequences, sent+bounced can exceed total leads, so cap at 100%
  const totalProcessed = stats.sent + (stats.bounced || 0);
  const completionPercent = stats.total > 0 ? Math.min(100, Math.round((totalProcessed / stats.total) * 100)) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: 'rgba(33, 150, 243, 0.05)',
        border: '1px solid rgba(33, 150, 243, 0.2)',
        borderRadius: 2,
      }}
    >
      {/* Header row */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <MailOutlineIcon sx={{ color: '#2196f3' }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Email Campaign Progress
        </Typography>
        {emailCampaignId && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Campaign ID: {emailCampaignId.slice(0, 8)}...
          </Typography>
        )}
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            Sending Progress
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {totalProcessed.toLocaleString()} / {stats.total.toLocaleString()} ({completionPercent}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={completionPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'rgba(33, 150, 243, 0.15)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#2196f3',
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Stats row */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        sx={{
          '& > *': {
            flex: '1 1 auto',
            minWidth: 'fit-content',
          },
        }}
      >
        <StatItem
          label="Sent"
          value={stats.sent}
          icon={<SendIcon sx={{ fontSize: 20 }} />}
          color="#2196f3"
          bgColor="rgba(33, 150, 243, 0.1)"
        />

        <StatItem
          label="Opened"
          value={stats.opened}
          rate={stats.open_rate}
          icon={<DraftsIcon sx={{ fontSize: 20 }} />}
          color="#4caf50"
          bgColor="rgba(76, 175, 80, 0.1)"
        />

        <StatItem
          label="Clicked"
          value={stats.clicked}
          rate={stats.click_rate}
          icon={<MouseIcon sx={{ fontSize: 20 }} />}
          color="#9c27b0"
          bgColor="rgba(156, 39, 176, 0.1)"
        />

        <StatItem
          label="Replied"
          value={stats.replied}
          rate={stats.reply_rate}
          icon={<ReplyIcon sx={{ fontSize: 20 }} />}
          color="#00bcd4"
          bgColor="rgba(0, 188, 212, 0.1)"
        />

        <StatItem
          label="Bounced"
          value={stats.bounced}
          icon={<ErrorOutlineIcon sx={{ fontSize: 20 }} />}
          color="#f44336"
          bgColor="rgba(244, 67, 54, 0.1)"
        />
      </Box>
    </Paper>
  );
};

export default EmailCampaignStats;
